import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import api from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { ArrowRight, MapPin, Calendar, Plane, Handshake, FileCheck, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ToursPage() {
  const { t, tData, locale, currency, convertPrice, formatPrice } = useLocale();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const localized = (tour, key) => {
    if (locale === "en" && tour[`${key}_en`]) return tour[`${key}_en`];
    if (locale === "es" && tour[`${key}_es`]) return tour[`${key}_es`];
    return tour[key];
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-secondary/30">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-20 lg:py-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-6 flex items-center gap-3">
              <span className="h-px w-12 bg-accent" />
              {t("tours.eyebrow")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-foreground leading-[0.95]">
              {t("tours.public_title")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed text-pretty">
              {t("tours.public_subtitle")}
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-xl">
              <div>
                <Coffee className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground leading-snug">{t("tours.benefit_cupping")}</div>
              </div>
              <div>
                <Handshake className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground leading-snug">{t("tours.benefit_producers")}</div>
              </div>
              <div>
                <FileCheck className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground leading-snug">{t("tours.benefit_diligence")}</div>
              </div>
              <div>
                <Plane className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground leading-snug">{t("tours.benefit_logistics")}</div>
              </div>
            </div>
            <div className="mt-8 border-l-2 border-accent pl-4 text-xs text-muted-foreground/90 max-w-lg">
              {t("tours.eligibility_note")}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="aspect-[4/3] border border-border overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=900&h=700"
                alt="Sul de Minas"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="mx-auto max-w-[1600px] w-full px-4 sm:px-8 py-16 flex-1">
        {loading ? (
          <div className="text-center text-muted-foreground font-mono-data text-xs uppercase tracking-wider py-20">
            {t("common.loading")}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {tours.map((tour) => (
              <Link
                key={tour.id}
                to={`/tours/${tour.id}`}
                className="group block hover-lift border border-border bg-card overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={tour.hero_image}
                    alt={localized(tour, "name")}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <MapPin className="h-3 w-3" /> {tData("region", tour.region)}, {tour.country}
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl tracking-tight font-black leading-tight group-hover:text-accent transition-colors">
                    {localized(tour, "name")}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
                    {localized(tour, "summary")}
                  </p>

                  <div className="mt-6 pt-6 border-t border-border grid grid-cols-3 gap-3">
                    <div>
                      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {t("tours.duration")}
                      </div>
                      <div className="font-mono-data text-sm mt-1">
                        {tour.duration_days || (tour.duration_options || [7])[0]} {t("tours.days")}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{t("tours.farms")}</div>
                      <div className="font-mono-data text-sm mt-1">{tour.farms?.length || 0} {t("tours.farms_count")}</div>
                    </div>
                    <div>
                      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{t("tours.from_price")}</div>
                      <div className="font-mono-data text-sm mt-1 text-accent">
                        {formatPrice(convertPrice(tour.price_per_person_usd || tour.price_per_person_7d_usd, "USD", currency), currency)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-xs text-muted-foreground font-mono-data uppercase tracking-wider">
                      {t("tours.see_route")}
                    </div>
                    <div className="text-accent group-hover:translate-x-1 transition-transform">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
